import { capitalize, cloneDeep, truncate } from "lodash";
import type { ErrorObject } from "@vuelidate/core";
import type FeedFormData from "@/shared/datamodels/feedFormData";
import { AssetType } from "@/shared/datamodels/asset";
import type { AdminAsset } from "@/api/model";
import { parseISO, format as formatDate } from "date-fns";
import dayjs from "dayjs";
import dayjsUtc from "dayjs/plugin/utc";
import dayjsTimezone from "dayjs/plugin/timezone";

dayjs.extend(dayjsUtc);
dayjs.extend(dayjsTimezone);

interface IOption {
  value: string;
  title: string;
}
export const convertObjectOptionToInt = (obj: IOption | string | number) => {
  // convert vuetify dropdown object to int
  let option = obj;
  if (typeof option === "object") {
    option = option.value;
  }
  return parseInt(option.toString());
};

export const feedFormToPostData = (form: FeedFormData) => {
  const data = cloneDeep(form);
  // @ts-expect-error - property exists in runtime model
  data.training_collection_id = convertObjectOptionToInt(
    // @ts-expect-error - runtime property
    data?.training_collection_id
  );
  for (let i = 0; i < ((data.attachments as any[]) ?? []).length; i++) {
    if ((data.attachments as any[])[i]?.id) {
      delete (data.attachments as any[])[i]["id"];
    }
  }
  return data;
};

export const enumToSelectOptions = (
  enumValue: any,
  returnObject: boolean = true
) => {
  const optionsObject: { name: string; code: string }[] = [];
  const arr: string[] = [];
  let key: keyof typeof enumValue;
  for (key in enumValue) {
    const value = enumValue[key];
    optionsObject.push({
      name: capitalize(value),
      code: value,
    });
    arr.push(value);
  }
  return returnObject ? optionsObject : arr;
};

export const v$ErrorMessage = (
  errors: ErrorObject[]
): string | string[] | undefined => {
  if (!errors) return undefined;

  return errors.length > 0 ? errors[0].$message.toString() : undefined;
};

export const getNameInitials = (fullName: string) => {
  try {
    const [firstName = "", lastName = ""] = fullName.split(" ");
    return `${firstName.substring(0, 1)}${lastName.substring(0, 1)}`.toUpperCase();
  } catch (e) {
    return "";
  }
};

export const getAssetThumbnail = (asset: AdminAsset, size: number = 300) => {
  if (asset?.type === AssetType.image && asset?.file?.thumbnail_url_tpl) {
    return getThumbnailUrl(asset.file.thumbnail_url_tpl, size);
  }
  if (
    asset?.type === AssetType.video &&
    asset?.video?.cover?.thumbnail_url_tpl
  ) {
    return getThumbnailUrl(asset.video.cover.thumbnail_url_tpl, size);
  }
  if (asset?.file?.thumbnail_url_tpl) {
    return getThumbnailUrl(asset.file.thumbnail_url_tpl, size);
  }
  return undefined;
};

export const getThumbnailUrl = (url: string, size: number = 300) => {
  const s = size.toString();
  return (url || "").replace("{width}", s).replace("{height}", s);
};

/**
 * Calculates the booking total after applying a benefit discount.
 * Uses floor rounding on the discount to match the backend's ROUND_DOWN behavior
 * (see benefit_application_service.py _calculate_discount).
 */
export const calculateBookingTotal = (
  serviceAmount: number,
  discountPercentage: number = 0
): number => {
  if (discountPercentage <= 0) return serviceAmount;
  const discount =
    Math.floor(serviceAmount * (discountPercentage / 100) * 100) / 100;
  return Math.max(serviceAmount - discount, 0);
};

export const getFormattedCurrency = (
  value: number,
  alwaysShowCents: boolean = true
) => {
  // Check if the value has cents (is not a whole number)
  const hasCents = value % 1 !== 0;

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    // Show cents only if they are present
    minimumFractionDigits: alwaysShowCents || hasCents ? 2 : 0,
    maximumFractionDigits: alwaysShowCents || hasCents ? 2 : 0,
  }).format(value);
};

export const getFormattedPercentage = (value: number) => {
  return Intl.NumberFormat("en-US", {
    style: "percent",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
};

export enum DateFormat {
  DEFAULT = "MMM dd, yyyy",
  SHORT = "MM/dd/yy",
  DATETIME = "MMM dd, yyyy h:mm a",
  TIME = "h:mm a",
}

/**
 * Returns a Date whose local fields match the wall-clock time of the given
 * instant in the target IANA timezone. Formatting the result with date-fns
 * then yields the date/time as seen in that timezone, with DST handled by the
 * Intl engine. This mirrors what date-fns-tz's `toZonedTime` does, without
 * adding a dependency.
 */
export const getZonedDate = (date: Date, timeZone: string): Date => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(date);

  const partValue = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((part) => part.type === type)?.value);

  return new Date(
    partValue("year"),
    partValue("month") - 1,
    partValue("day"),
    partValue("hour") % 24,
    partValue("minute"),
    partValue("second")
  );
};

/**
 * Inverse of `getZonedDate`: interprets the local wall-clock fields of `date`
 * as a time in the given IANA timezone and returns the corresponding instant.
 * Mirrors date-fns-tz's `fromZonedTime`, using dayjs (already a dependency)
 * so DST transitions are handled correctly.
 */
export const getUtcFromZonedDate = (date: Date, timeZone: string): Date =>
  dayjs.tz(formatDate(date, "yyyy-MM-dd HH:mm:ss"), timeZone).toDate();

export const getFormattedDate = (
  date: string | Date | null | undefined,
  format: string | DateFormat = DateFormat.DEFAULT,
  timeZone?: string
): string => {
  if (!date) return "";

  try {
    const dateObj = typeof date === "string" ? parseISO(date) : date;
    const zonedDate = timeZone ? getZonedDate(dateObj, timeZone) : dateObj;
    return formatDate(zonedDate, format);
  } catch (e) {
    console.error("Error formatting date:", date, e);
    return "Invalid Date";
  }
};

export const getFormattedTime = (
  date: string | Date | null | undefined,
  timeZone?: string
): string => {
  return getFormattedDate(date, DateFormat.TIME, timeZone);
};

/**
 * Returns the short timezone abbreviation (e.g. "MST", "EDT") for the given
 * date. When `timeZone` (an IANA name) is provided the abbreviation is resolved
 * in that timezone; otherwise the browser's local timezone is used. Deriving it
 * from the date itself handles DST correctly.
 */
export const getFormattedTimezone = (
  date: string | Date | null | undefined,
  timeZone?: string
): string => {
  if (!date) return "";

  try {
    const dateObj = typeof date === "string" ? parseISO(date) : date;
    if (Number.isNaN(dateObj.getTime())) return "";
    return (
      dateObj
        .toLocaleTimeString("en-US", {
          timeZoneName: "short",
          ...(timeZone ? { timeZone } : {}),
        })
        .split(" ")
        .at(-1) || ""
    );
  } catch (e) {
    console.error("Error formatting timezone:", date, e);
    return "";
  }
};

export const getTruncatedBreadcrumb = (label: string) => {
  const MAX_BREADCRUMB_LENGTH = 50;
  if (label.length > MAX_BREADCRUMB_LENGTH) {
    return label.substring(0, MAX_BREADCRUMB_LENGTH) + "...";
  }
  return label;
};

/**
 * Formats an API time string (HH:MM:SS or HH:MM) to HH:MM format.
 * @param timeStr - The time string from the API.
 * @returns The formatted time string (HH:MM) or null if input is invalid.
 */
export const formatApiTime = (
  timeStr: string | null | undefined
): string | null => {
  if (!timeStr) return null;
  // Regex to capture HH:MM from HH:MM:SS or HH:MM
  const match = timeStr.match(/^(\d{2}:\d{2})/);
  return match ? match[1] : null;
};

export const truncateString = (str: string, maxLength: number) => {
  return truncate(str, {
    length: maxLength,
    omission: "...",
  });
};

/**
 * Parses a "yyyy-MM-dd" string into a Date in local time (not UTC).
 * Avoids the timezone shift caused by `new Date("yyyy-MM-dd")` which
 * parses as UTC midnight.
 */
export const dateStringToLocalDate = (dateString: string): Date => {
  const [year, month, day] = dateString.split("-").map(Number);
  return new Date(year, month - 1, day);
};

/**
 * Formats a Date object to "yyyy-MM-dd" using local time (not UTC).
 * Avoids the timezone shift caused by `toISOString().split("T")[0]`.
 */
export const localDateToDateString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Converts a Date object or "yyyy-MM-dd" string to a "yyyy-MM-dd" string
 * using local time. Handles both input types safely without UTC shift.
 */
export const toDateString = (value: string | Date): string => {
  if (typeof value === "string") return value;
  return localDateToDateString(value);
};

export const downloadBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const sanitizeFileName = (fileName: string): string => {
  return fileName.replace(/[^a-zA-Z0-9._]/g, "_");
};
